import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProgressTracker from "../../../../utils/ProgressTracker";
import { Checkbox } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../../../../components/common/loader/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import {
  updateOngoingDescription,
  updateOngoingHardwareParts,
} from "../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import { fetchWorkEntriesProjectTracking } from "../../../../store/slices/workentriesSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../store/slices/DescriptionTask";
import { useSnackbar } from "notistack";
import {
  markDescriptionComplete,
  markHardwarePartComplete,
  markNotDescriptionComplete,
  markNotHardwarePartComplete,
} from "../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { fetchHardwareProjectTracking } from "../../../../store/slices/hardwareDescriptionSlice";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import DateConversion from "../../../../utils/DateConversion";
import CommaConvert from "../../../../utils/CommaConvert";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";
import { useLoggedUser } from "../../../../utils/useLoggedUser";
import {
  parseSavedOngoingExpenseRaw,
  getOngoingExpenseNumberForTracker,
} from "../../../../utils/trainingOtherExpenseSync";
import useToolGuideSync from "../../../../features/toolGuide/useToolGuideSync";
import { MANAGER_BUCKET_KEYS } from "../../../../features/toolGuide/toolGuideConfig";

const getOngoingExpenseInputDisplay = (entry, draft) => {
  if (draft != null && String(draft).trim() !== "") return draft;
  const saved = parseSavedOngoingExpenseRaw(entry);
  if (saved == null) return "";
  return formatAmountForInput(saved);
};

const isDescriptionTaskComplete = (entry) =>
  entry?.isTaskComplete === true || entry?.isTaskcomplete === true;

const OPHardwareOngoing = ({
  refresh,
  setIsLoading,
  index,
  row,
  hardwareProjectTrackingParts,
  hardwareDescriptions,
  showListState,
  isViewOnly = false,
}) => {
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } =
    DateConversion();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { syncToolGuideStep } = useToolGuideSync(
    MANAGER_BUCKET_KEYS.PROJECT_ONGOING,
    "L4_OPS"
  );
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const loggedUser = useLoggedUser();
  const [trainingEndDate, setTrainingEndDate] = useState(null);

  const [loadingRows, setLoadingRows] = useState({});
  const [descriptionCompleteChecked, setDescriptionCompleteChecked] =
    useState({});

  const lockedTrainingRow = useMemo(() => {
    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const normKey = (v) => String(v || "").trim().toLowerCase();
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const pick = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k];
        if (val != null && String(val).trim() !== "") return val;
      }
      return "";
    };

    if (!row || trainingsList.length === 0) return null;

    const loggedEmail = String(
      loggedUser?.EmpEmail || loggedUser?.email || localStorage.getItem("email") || ""
    ).trim().toLowerCase();
    const loggedEmployeeId = resolveRefId(loggedUser?._id || loggedUser?.id);
    const assigneeEmail = loggedEmail;
    if (!assigneeEmail && !loggedEmployeeId) return null;

    const leadLike = row?.lead || row?.Lead || null;
    const projectIdCandidates = new Set(
      [
        resolveRefId(pick(row, ["_id", "id", "ProjectID", "projectid", "projectId", "ProjectId"])),
        resolveRefId(pick(leadLike, ["_id", "id"])),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const quotationIdCandidates = new Set(
      [
        pick(row, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
        pick(leadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );
    const projectNameCandidates = new Set(
      [pick(row, ["ProjectName", "projectname"]), pick(leadLike, ["ProjectName", "projectname"])]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const leadIdCandidates = new Set(
      [pick(row, ["leadid", "leadId", "LeadId", "LeadID"]), pick(leadLike, ["leadid", "leadId", "LeadId", "LeadID"])]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const matched = trainingsList.filter((t) => {
      const assignedToRaw = t?.assignedto ?? t?.assignedTo;
      const assignedToId = resolveRefId(assignedToRaw);
      const assignedToEmail =
        typeof assignedToRaw === "string" && assignedToRaw.includes("@")
          ? normKey(assignedToRaw)
          : normKey(
              assignedToRaw?.EmpEmail ||
                assignedToRaw?.email ||
                t?.assignedToEmail ||
                t?.assigneeEmail ||
                ""
            );
      const assigneeMatches =
        (loggedEmployeeId && assignedToId && String(assignedToId) === String(loggedEmployeeId)) ||
        (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
      if (!assigneeMatches) return false;

      const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
      const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
      const tProjectName = normKey(t?.projectname);
      const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

      return (
        (tProjectId && projectIdCandidates.has(tProjectId)) ||
        (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
        (tProjectName && projectNameCandidates.has(tProjectName)) ||
        (tLeadId && leadIdCandidates.has(tLeadId))
      );
    });

    const normalized = matched.map((t) => {
      const amount = Number(String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, ""));
      const notes = t?.notes ?? t?.note ?? "";
      const parseDate = (d) => {
        if (!d) return null;
        const dt = d instanceof Date ? d : new Date(d);
        return isNaN(dt.getTime()) ? null : dt;
      };
      const start =
        parseDate(t?.startdate ?? t?.startDate ?? t?.duedate ?? t?.dueDate ?? t?.DueDate) ??
        null;
      const end =
        parseDate(t?.enddate ?? t?.endDate ?? t?.EndDate) ?? null;
      return {
        amount: Number.isFinite(amount) ? amount : 0,
        notes: String(notes || "").trim(),
        start,
        end,
      };
    });

    const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
    if (total <= 0 && !joinedNotes) return null;

    const startDate = normalized
      .map((x) => x.start)
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime())[0] || null;
    const endDate = normalized
      .map((x) => x.end)
      .filter(Boolean)
      .sort((a, b) => b.getTime() - a.getTime())[0] || null;

    return {
      description: "Training Cost",
      value: total,
      notes: joinedNotes,
      startDate,
      endDate,
    };
  }, [mytrainings, row, loggedUser]);

  useEffect(() => {
    setTrainingEndDate(lockedTrainingRow?.endDate || null);
  }, [lockedTrainingRow?.endDate]);

  useEffect(() => {
    setDescriptionCompleteChecked((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      let changed = false;
      (hardwareDescriptions || []).forEach((entry) => {
        const rowKey = entry?._id;
        if (!rowKey || next[rowKey] === undefined) return;
        const serverComplete = isDescriptionTaskComplete(entry);
        if (serverComplete === next[rowKey]) {
          delete next[rowKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [hardwareDescriptions]);

  const handleHardwarePartsMarkComplete = async (entry) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markHardwarePartComplete({ projectid, taskid }));
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonCompleteHardware = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNotHardwarePartComplete({ projectid, taskid }));
      dispatch(fetchHardwareProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Part Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const [updatedHardwareEntries, setUpdatedHardwareEntries] = useState({});
  const handleDateSelfEntryHardware = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      syncToolGuideStep("view-details");
      const matchedEntry = updatedHardwareEntries[entry._id] || {}; // safely get data
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;
      const updatedData = {
        description: entry.assemblepartdescription,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
        // startdate: matchedEntry.startdate|| matchedStartDate,
        // enddate: matchedEntry.enddate || matchedEndDate,
      };
      try {
        await dispatch(
          updateOngoingHardwareParts({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
      } finally {
        refresh();
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [
      updatedHardwareEntries,
      row,
      dispatch,
      setIsLoading,
      refresh,
      enqueueSnackbar,
      isViewOnly,
      syncToolGuideStep,
    ]
  );
  const handleDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedHardwareEntries((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));
      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDateSelfEntryHardware(newentry);
      }
    },
    [handleDateSelfEntryHardware, isViewOnly]
  );

  const [updatedDateDescription, setUpdatedDateDescription] = useState({});
  const handleDescriptionDateSubmit = useCallback(
    async (entry) => {
      if (isViewOnly) return;
      syncToolGuideStep("view-details");
      const matchedEntry = updatedDateDescription[entry._id] || {};
      const matchedStartDate = matchedEntry.startdate || null;
      const matchedEndDate = matchedEntry.enddate || null;

      const updatedData = {
        description: entry.description,
        value: entry.value,
        notes: entry.notes,
        typeservice: entry.typeservice,
        // startdate: matchedEntry.startdate || matchedStartDate,
        // enddate: matchedEntry.enddate || matchedEndDate,
        startdate: matchedStartDate ?? entry.startdate ?? null,
        enddate: matchedEndDate ?? entry.enddate ?? null,
      };

      try {
        await dispatch(
          updateOngoingDescription({
            projectId: row?._id,
            id: entry._id,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
        dispatch(fetchHardwareProjectTracking());
      } catch {
        enqueueSnackbar("Failed To Add Date", { variant: "error" });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
        setIsLoading(false);
      }
    },
    [updatedDateDescription, row, dispatch, enqueueSnackbar, setIsLoading, isViewOnly, syncToolGuideStep]
  );

  const handleDescriptionDateChange = useCallback(
    (date, field, entry) => {
      if (isViewOnly) return;
      setUpdatedDateDescription((prev) => ({
        ...prev,
        [entry._id]: {
          ...prev[entry._id],
          [field]: date,
        },
      }));

      if (field === "enddate") {
        const newentry = {
          ...entry,
          enddate: date,
        };
        handleDescriptionDateSubmit(newentry);
      }
    },
    [handleDescriptionDateSubmit, isViewOnly]
  );

  const [editedDescription, setEditedDescription] = useState({});
  const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});

  const isOverdueIncomplete = (entry) => {
    const isIncomplete =
      entry?.isTaskComplete === false || entry?.isTaskcomplete === false;
    if (!isIncomplete || !entry?.enddate) return false;
    const endDate = new Date(entry.enddate);
    if (isNaN(endDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const handleDescriptionExpensesUpdate = useCallback(
    async (entryId, { manageLoading = true } = {}) => {
      if (isViewOnly) return false;
      syncToolGuideStep("view-details");
      const value = editedDescription[entryId];
      if (!entryId || value === undefined) return true;
      const updatedData = {
        expenses: parseAmountNumber(value),
      };
      if (manageLoading) {
        setLoadingRows((prev) => ({ ...prev, [entryId]: true }));
        setIsLoading(true);
      }
      try {
        await dispatch(
          updateOngoingDescription({
            projectId: row?._id,
            id: entryId,
            updatedData,
          })
        );
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
        dispatch(fetchHardwareProjectTracking());
        return true;
      } catch {
        enqueueSnackbar("Failed To Add Expense", { variant: "error" });
        return false;
      } finally {
        if (manageLoading) {
          setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
          setIsLoading(false);
        }
      }
    },
    [isViewOnly, editedDescription, row?._id, dispatch, enqueueSnackbar, setIsLoading, syncToolGuideStep]
  );

  const handleDescriptionNotesUpdate = async (entryId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    const value = editedDescriptionNotes[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      notes: value,
    };
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleHardwareQuantityUpdate = async (entryId) => {
    if (isViewOnly) return;
    syncToolGuideStep("view-details");
    if (!entryId) return;

    const entryData = updatedHardwareEntries[entryId];
    if (!entryData) return;

    const updatedData = {
      quantity: entryData.quantity,
      totalprice: entryData.totalprice,
    };
    console.log("handleHardwareQuantityUpdate entryId", entryId, updatedData);
    try {
      setLoadingRows((prev) => ({ ...prev, [entryId]: true }));

      await dispatch(
        updateOngoingHardwareParts({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );

      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch (error) {
      enqueueSnackbar("Failed to update quantity", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleOtherExpenseCheckboxClick = useCallback(
    async (entry, serverComplete) => {
      if (isViewOnly || loadingRows[entry._id]) return;
      syncToolGuideStep("view-details");

      const rowKey = entry._id;
      const revertCheckbox = () => {
        setDescriptionCompleteChecked((prev) => ({
          ...prev,
          [rowKey]: serverComplete,
        }));
      };

      const nextChecked = !(
        descriptionCompleteChecked[rowKey] ?? serverComplete
      );
      setDescriptionCompleteChecked((prev) => ({
        ...prev,
        [rowKey]: nextChecked,
      }));

      const currentExpenses = getOngoingExpenseNumberForTracker(
        entry,
        editedDescription[entry._id]
      );
      const startDate =
        updatedDateDescription[entry._id]?.startdate || entry.startdate;
      const endDate =
        updatedDateDescription[entry._id]?.enddate || entry.enddate;

      if (nextChecked) {
        if (!startDate || !endDate) {
          revertCheckbox();
          enqueueSnackbar("Please Select End Date", { variant: "error" });
          return;
        }
        if (!currentExpenses || currentExpenses <= 0) {
          revertCheckbox();
          enqueueSnackbar("Please Enter Expense", { variant: "error" });
          return;
        }

        setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));
        setIsLoading(true);
        try {
          await dispatch(
            updateOngoingDescription({
              projectId: row?._id,
              id: entry._id,
              updatedData: {
                description: entry.description,
                value: entry.value,
                notes: editedDescriptionNotes[entry._id] ?? entry.notes ?? "",
                typeservice: entry.typeservice,
                startdate: startDate ?? entry.startdate ?? null,
                enddate: endDate ?? entry.enddate ?? null,
                expenses: parseAmountNumber(
                  editedDescription[entry._id] ?? entry.expenses
                ),
              },
            })
          ).unwrap();
          await dispatch(
            markDescriptionComplete({ projectid: row?._id, taskid: entry._id })
          ).unwrap();
          dispatch(fetchWorkEntriesProjectTracking());
          dispatch(fetchDescriptionTasksProjectTracking());
          dispatch(fetchHardwareProjectTracking());
          refresh();
          enqueueSnackbar("Description Completed Successfully", {
            variant: "success",
          });
        } catch {
          revertCheckbox();
          enqueueSnackbar("Failed To Complete Task", { variant: "error" });
        } finally {
          setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
          setIsLoading(false);
        }
        return;
      }

      setLoadingRows((prev) => ({ ...prev, [rowKey]: true }));
      setIsLoading(true);
      try {
        await dispatch(
          markNotDescriptionComplete({ projectid: row?._id, taskid: entry._id })
        ).unwrap();
        dispatch(fetchWorkEntriesProjectTracking());
        dispatch(fetchDescriptionTasksProjectTracking());
        dispatch(fetchHardwareProjectTracking());
        refresh();
        enqueueSnackbar("Description Not-Completed Successfully", {
          variant: "success",
        });
      } catch {
        revertCheckbox();
        enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
      } finally {
        setLoadingRows((prev) => ({ ...prev, [rowKey]: false }));
        setIsLoading(false);
      }
    },
    [
      isViewOnly,
      loadingRows,
      descriptionCompleteChecked,
      editedDescription,
      editedDescriptionNotes,
      updatedDateDescription,
      enqueueSnackbar,
      setIsLoading,
      refresh,
      dispatch,
      row?._id,
      syncToolGuideStep,
    ]
  );

  return (
    <div onClick={(e) => e.stopPropagation()} className="w-full flex-col">
      {showListState === "tasktable" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden border">
          <div className="sticky top-0 z-[999] flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal">
            <div className="p-1 w-[10%] text-center">Part Name</div>
            <div className="p-1 w-[10%]">Part No</div>
            <div className="p-1 w-[10%] text-center">{" Quantity"} </div>
            <div className="p-1 w-[10%] text-center">
              Total Price ({currency})
            </div>{" "}
            <div className="p-1 w-[10%] text-center">Start Date</div>
            <div className="p-1 w-[10%]">End Date</div>
            <div className="p-1 w-[30%] text-center">Tracker</div>
            <div className="p-1 w-[10%] text-center">Action</div>
          </div>

          <div className="flex text-[10px] break-all whitespace-normal max-h-14">
            <div className="p-1 text-center border-b border-[#dee2e6] w-[10%]">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%]"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {USAComma(
                hardwareProjectTrackingParts.reduce(
                  (sum, item) => sum + (item?.quantity || 0),
                  0
                )
              )}{" "}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {USAComma(
                hardwareProjectTrackingParts.reduce(
                  (sum, item) => sum + (item?.totalprice || 0),
                  0
                )
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
              {(() => {
                const allEntries = hardwareProjectTrackingParts.length;
                const completedEntriesCount =
                  hardwareProjectTrackingParts.filter(
                    (entry) => entry.isTaskComplete
                  ).length;
                const trackerPercentage =
                  allEntries > 0
                    ? (completedEntriesCount / allEntries) * 100
                    : 0;
                return <ProgressTracker value={trackerPercentage} />;
              })()}
            </div>

            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
          </div>

          {hardwareProjectTrackingParts.length > 0 ? (
            hardwareProjectTrackingParts.map((entry, index) => (
              (() => {
                const showOverdueWarning = isOverdueIncomplete(entry);
                return (
              <div
                key={entry}
                title={showOverdueWarning ? "end date is passed" : ""}
                className={`flex text-[10px] break-all whitespace-normal max-h-14 ${
                  ""
                }`}
              >
                <div
                  className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14"
                  title={`${entry.assemblepartdescription}`}
                >
                  {entry.assemblepartdescription}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {entry.partno}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {/* {entry.quantity} */}
                  {entry.isTaskComplete ? (
                    <span>{entry.quantity || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={
                        updatedHardwareEntries[entry._id]?.quantity ??
                        entry.quantity ??
                        ""
                      }
                      placeholder="enter quantity"
                      className={`border border-gray-200 w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const qty = Number(e.target.value) || 0;

                        setUpdatedHardwareEntries((prev) => ({
                          ...prev,
                          [entry._id]: {
                            ...prev[entry._id],
                            quantity: qty,
                            totalprice: qty * entry.unitprice,
                          },
                        }));
                      }}
                      onBlur={() => handleHardwareQuantityUpdate(entry._id)}
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {USAComma(
                    updatedHardwareEntries[entry._id]?.totalprice ??
                      entry.totalprice ??
                      0
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        entry.startdate
                          ? new Date(entry.startdate)
                          : row?.DateRequested
                            ? new Date(row.DateRequested)
                            : null
                      }
                      maxDate={new Date()}
                      selected={
                        updatedHardwareEntries[entry._id]?.startdate
                          ? updatedHardwareEntries[entry._id].startdate
                          : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                      }
                      onChange={(date) =>
                        handleDateChange(date, "startdate", entry)
                      }
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 ${
                        showOverdueWarning ? "border-orange-400" : "border-gray-300"
                      } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        updatedHardwareEntries[entry._id]?.startdate
                          ? new Date(
                              updatedHardwareEntries[entry._id].startdate
                            )
                          : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                      }
                      selected={
                        updatedHardwareEntries[entry._id]?.enddate
                          ? updatedHardwareEntries[entry._id].enddate
                          : entry.enddate
                          ? new Date(entry.enddate)
                          : null
                      }
                      onChange={(date) =>
                        handleDateChange(date, "enddate", entry)
                      }
                    />
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                  <ProgressTracker value={entry.isTaskComplete ? 100 : 0} />
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {loadingRows[entry._id] ? (
                    <LoadingSpinner />
                  ) : (
                    <Checkbox
                      checked={!!entry.isTaskComplete}
                      disabled={
                        isViewOnly ||
                        !!loadingRows[entry._id] ||
                        isDescriptionTaskComplete(entry) ||
                        row.Assignedperson?.every(
                          (person) => person.isProjectComplete === true
                        )
                      }
                      sx={
                        entry.isTaskComplete
                          ? {
                              color: "grey.500",
                              "&.Mui-checked": { color: "grey.600" },
                            }
                          : undefined
                      }
                      onChange={async (e) => {
                        if (isViewOnly || loadingRows[entry._id]) return;
                        const isChecked = e.target.checked;
                        const startDate =
                          updatedHardwareEntries[entry._id]?.startdate ||
                          entry.startdate;
                        const endDate =
                          updatedHardwareEntries[entry._id]?.enddate ||
                          entry.enddate;
                        if (isChecked) {
                          if (!startDate || !endDate) {
                            enqueueSnackbar("Please Select End Date", {
                              variant: "error",
                            });
                            return;
                          }
                          await handleHardwarePartsMarkComplete(entry);
                          await handleDateSelfEntryHardware(entry);
                        } else {
                          await handleMarkNonCompleteHardware(entry);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            );
              })()
            ))
          ) : (
            <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}
        </div>
      )}

      {showListState === "otherexpenses" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden border">
          <div className="sticky z-[999] top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-all whitespace-normal ">
            <div className="p-1 w-[10%] text-center">Description</div>
            <div className="p-1 w-[10%] text-center">Amount ({currency})</div>

            <div className="p-1 w-[10%] text-center">Start Date</div>
            <div className="p-1 w-[10%]">End Date</div>
            <div className="p-1 w-[10%] text-center">Expenses ({currency})</div>
            <div className="p-1 w-[10%] text-center">Notes</div>
            <div className="p-1 w-[30%] text-center">Tracker</div>
            <div className="p-1  w-[10%] text-center">Action</div>
          </div>

          <div className="flex text-[10px] break-all whitespace-normal max-h-14">
            <div className="p-1 text-center border-b border-[#dee2e6] w-[10%]">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%]">
              {formatAmountComma(
                hardwareDescriptions.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.value),
                  0
                ) + parseAmountNumber(lockedTrainingRow?.value || 0)
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {formatAmountComma(
                hardwareDescriptions.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.expenses),
                  0
                ) + parseAmountNumber(lockedTrainingRow?.value || 0)
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
              {(() => {
                const list = lockedTrainingRow
                  ? [
                      ...hardwareDescriptions,
                      {
                        value: lockedTrainingRow.value,
                        expenses: lockedTrainingRow.value,
                        isTaskComplete: true,
                      },
                    ]
                  : hardwareDescriptions;

                const hoursAssigned = list.reduce(
                  (sum, item) => sum + (item?.value || 0),
                  0
                );
                const hoursSpent = list.reduce((sum, item) => {
                  if (!item?.description && item?.expenses != null) {
                    return sum + (Number(item.expenses) || 0);
                  }
                  return sum + (parseSavedOngoingExpenseRaw(item) ?? 0);
                }, 0);

                const trackerPercentage =
                  hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;

                const displayValue = list.every((entry) => entry.isTaskComplete)
                  ? trackerPercentage < 100
                    ? 100
                    : trackerPercentage
                  : trackerPercentage;

                return <ProgressTracker value={displayValue} />;
              })()}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center"></div>
          </div>

          {hardwareDescriptions.length > 0 ? (
            hardwareDescriptions.map((entry, index) => (
              (() => {
                const showOverdueWarning = isOverdueIncomplete(entry);
                const serverComplete = isDescriptionTaskComplete(entry);
                const checkboxChecked =
                  descriptionCompleteChecked[entry._id] ?? serverComplete;
                return (
              <div
                key={entry._id || index}
                title={showOverdueWarning ? "end date is passed" : ""}
                className={`flex text-[10px] break-all whitespace-normal max-h-14 ${
                  ""
                }`}
              >
                <div
                  className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14"
                  title={`${entry.description}`}
                >
                  {entry.description}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {formatAmountComma(entry.value)}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        entry.startdate
                          ? new Date(entry.startdate)
                          : row?.DateRequested
                            ? new Date(row.DateRequested)
                            : null
                      }
                      maxDate={new Date()}
                      selected={
                        updatedDateDescription[entry._id]?.startdate
                          ? updatedDateDescription[entry._id].startdate
                          : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                      }
                      onChange={(date) =>
                        handleDescriptionDateChange(date, "startdate", entry)
                      }
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete  ? (
                    <div className="text-black">
                      {entry.enddate ? formatDateByCountry(entry.enddate) : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 ${
                        showOverdueWarning ? "border-orange-400" : "border-gray-300"
                      } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        updatedDateDescription[entry._id]?.startdate
                          ? new Date(
                              updatedDateDescription[entry._id].startdate
                            )
                          : entry.startdate
                          ? new Date(entry.startdate)
                          : null
                      }
                      selected={
                        updatedDateDescription[entry._id]?.enddate
                          ? updatedDateDescription[entry._id].enddate
                          : entry.enddate
                          ? new Date(entry.enddate)
                          : null
                      }
                      onChange={(date) =>
                        handleDescriptionDateChange(date, "enddate", entry)
                      }
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete ? (
                    <span>{formatAmountComma(entry.expenses) || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={getOngoingExpenseInputDisplay(
                        entry,
                        editedDescription[entry._id]
                      )}
                      placeholder="enter expense"
                      className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = validateAmountValue(e.target.value);
                        const amountLimit = parseAmountNumber(entry.value);
                        const expenseNum = parseAmountNumber(value);
                        if (
                          value !== "" &&
                          amountLimit > 0 &&
                          expenseNum > amountLimit
                        ) {
                          enqueueSnackbar("Expenses is exceeding the Amount", {
                            variant: "error",
                          });
                        }
                        if (expenseNum < 0) return;
                        setEditedDescription((prev) => ({
                          ...prev,
                          [entry._id]: value, // store by task id
                        }));
                      }}
                      onBlur={() => handleDescriptionExpensesUpdate(entry._id)}
                    />
                  )}
                </div>
                <div
                  className="p-1 border-b border-[#dee2e6] w-[10%] text-center max-h-14 overflow-auto"
                  title={`${entry.notes}`}
                >
                  {/* {entry.notes} */}
                  {entry.isTaskComplete ? (
                    <span>{entry.notes || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={
                        editedDescriptionNotes[entry._id] ?? entry.notes ?? ""
                      }
                      placeholder="enter note"
                      className={`border border-gray-200 placeholder-text-gray-400 text-black w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        setEditedDescriptionNotes((prev) => ({
                          ...prev,
                          [entry._id]: value,
                        }));
                      }}
                      onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                    />
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                  {entry.isTaskComplete ? (
                    <ProgressTracker
                      value={(() => {
                        const rawValue =
                          (getOngoingExpenseNumberForTracker(
                            entry,
                            editedDescription[entry._id]
                          ) /
                            entry.value) *
                          100;
                        return rawValue < 100 ? 100 : rawValue;
                      })()}
                    />
                  ) : (
                    <ProgressTracker
                      value={
                        (getOngoingExpenseNumberForTracker(
                          entry,
                          editedDescription[entry._id]
                        ) /
                          entry.value) *
                        100
                      }
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {loadingRows[entry._id] ? (
                    <LoadingSpinner />
                  ) : (
                    <Checkbox
                      disabled={
                        isViewOnly ||
                        !!loadingRows[entry._id] ||
                        row.Assignedperson?.every(
                          (person) => person.isProjectComplete === true
                        ) ||
                        (!serverComplete &&
                          (getOngoingExpenseNumberForTracker(
                            entry,
                            editedDescription[entry._id]
                          ) /
                            parseAmountNumber(entry.value)) *
                            100 <
                            1)
                      }
                      checked={checkboxChecked}
                      sx={
                        serverComplete
                          ? {
                              color: "grey.500",
                              "&.Mui-checked": { color: "grey.600" },
                            }
                          : undefined
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleOtherExpenseCheckboxClick(
                          entry,
                          serverComplete
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            );
              })()
            ))
          ) : (
            <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}

          {lockedTrainingRow && (
            <div
              title={lockedTrainingRow.notes ? lockedTrainingRow.notes : ""}
              className="flex text-[10px] break-all whitespace-normal max-h-14"
            >
              <div className="p-1 border-b border-[#dee2e6] w-[10%] overflow-auto max-h-14">
                {lockedTrainingRow.description}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                {formatAmountComma(lockedTrainingRow.value)}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                {row?.DateRequested
                  ? formatDateByCountry(row.DateRequested)
                  : lockedTrainingRow.startDate
                    ? formatDateByCountry(lockedTrainingRow.startDate)
                    : ""}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                <DatePicker
                  dateFormat={getDateFormat()}
                  disabled={isViewOnly}
                  className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${
                    isViewOnly
                      ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed"
                      : "bg-white"
                  }`}
                  placeholderText={getDatePlaceholder()}
                  popperClassName="z-[1500]"
                  minDate={
                    row?.DateRequested
                      ? new Date(row.DateRequested)
                      : lockedTrainingRow.startDate
                        ? new Date(lockedTrainingRow.startDate)
                        : null
                  }
                  selected={trainingEndDate}
                  onChange={(date) => {
                    if (isViewOnly) return;
                    setTrainingEndDate(date);
                  }}
                />
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                {formatAmountComma(lockedTrainingRow.value)}
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center max-h-14 overflow-auto">
                {lockedTrainingRow.notes || ""}
              </div>
              <div className="p-2.5 border-b border-[#dee2e6] w-[30%] text-center">
                <ProgressTracker value={100} />
              </div>
              <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center cursor-default">
                <Checkbox
                  checked
                  disabled
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": { color: "#1976d2" },
                    "&.Mui-disabled": { color: "#1976d2" },
                    "&.Mui-disabled.Mui-checked": { color: "#1976d2" },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OPHardwareOngoing;
